import React from "react";
import PropTypes from "prop-types";
import { useChatCurrentUser } from "./chat-current-user-context";
import { useChatNavigationContext } from "./chat-navigation-context";
import { usePrevious, useSafeSetState } from "@evertrue/et-components";
import { isDM } from "../utils/utils";
import { useChatDependencyContext } from "./chat-dependency-context";

const ChatRoomSubscriptionContext = React.createContext();
const sendBrowserNotification = (msg = "New message", cb = () => {}, app) => {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      const notification = new Notification("EverTrue", {
        body: msg,
        icon:
          app === "vms"
            ? "https://volunteer.evertrue.com/images/logo-icon-volunteers.png"
            : "https://s3.amazonaws.com/assets.evertrue.com/global/evertrue_logo_orange.png",
      });
      notification.addEventListener("click", function(e) {
        e.preventDefault();
        cb();
      });
    }
  } catch (error) {}
};

// We have to subscribe to all rooms here to get new message notifications
const ChatRoomSubscriptionProvider = ({ children }) => {
  const {
    currentChatUser: { rooms = [] },
    currentChatUser,
  } = useChatCurrentUser();
  const [nav_state, navDispatch] = useChatNavigationContext();
  const { app, isMobile } = useChatDependencyContext();
  const [messages, setMessages] = useSafeSetState({});

  const prev_nav_id = usePrevious(nav_state.currentRoomId);

  const subscribeToRoom = React.useCallback(
    roomId => {
      const setMessageWithNotification = (message, roomId) => {
        setMessages(prev => ({ ...prev, [roomId]: message }));
        const { room } = message;

        if (isMobile) return; // will throw, need to use service worker for mobile

        if (isDM(room.name) && message.sender.id != currentChatUser.id) {
          // if looking at dm room don't send notification, but send if panel is closed
          if (roomId == nav_state.currentRoomId && nav_state.panel_is_open) return;
          sendBrowserNotification("New message", () => navDispatch({ type: "SELECT_ROOM", payload: room.id }), app);
        }
      };
      return currentChatUser
        .subscribeToRoomMultipart({
          roomId,
          messageLimit: 0,
          hooks: {
            onMessage: message => {
              // just keep most recent for ChatHome
              setMessageWithNotification(message, roomId);
            },
          },
        })
        .then(room => {
          currentChatUser
            .fetchMultipartMessages({
              roomId: room.id,
              limit: 1,
            })
            .then(messages => {
              setMessages(prev => ({ ...prev, [roomId]: messages[0] }));
            });
        });
    },
    [currentChatUser, isMobile, nav_state.currentRoomId, app, navDispatch]
  );
  // diff this b/c if there was a previous one then we want to resubscribe at this level
  React.useEffect(() => {
    // explicit check b/c on android this code more than web
    if (prev_nav_id && nav_state.currentRoomId === null) {
      // check here to see if in current users rooms to subscribe
      // this is for the case where we call 'leave room' and don't want
      // to resubscribe
      const is_my_room = currentChatUser.rooms.find(room => room.id === prev_nav_id);
      if (is_my_room) {
        subscribeToRoom(prev_nav_id);
      }
    }
  }, [nav_state.currentRoomId, prev_nav_id, rooms, subscribeToRoom]);

  React.useEffect(() => {
    if (currentChatUser.id) {
      // this runs again when a user leaves a room/a room is removed
      // but just cancels old subscriptions so doesn't cause any isssues
      currentChatUser.rooms.forEach(room => {
        const roomId = room.id;

        subscribeToRoom(roomId); // this resolves the room in a promise but they are the same rooms as on currentUser.rooms so not duplicating state
      });
    }
  }, [currentChatUser.id]);

  const num_unread = currentChatUser.rooms && currentChatUser.rooms.reduce((acc, cur) => (acc += cur.unreadCount), 0);
  const value = React.useMemo(() => {
    return { messages, numUnread: num_unread };
  }, [messages, num_unread]);

  return <ChatRoomSubscriptionContext.Provider value={value}>{children}</ChatRoomSubscriptionContext.Provider>;
};
const useChatRoomSubscription = () => React.useContext(ChatRoomSubscriptionContext);

ChatRoomSubscriptionProvider.propTypes = {
  children: PropTypes.any,
};
export { ChatRoomSubscriptionProvider, useChatRoomSubscription };
export default ChatRoomSubscriptionContext;
